/**
 * NEXUMA — WorkClock (header time-clock control)
 *
 * Compact start / pause / end control for the top AppBar. Lets the logged-in
 * worker clock in ("Fillo punën"), take a short or long break ("Pauzë e
 * shkurtër" / "Pauzë e gjatë"), resume, and clock out ("Përfundo").
 *
 * State is derived from GET /api/attendance/active-session and mutated through
 * POST /api/attendanceEvents (via presenceService) — the same backend the
 * /presence-live and /scan flows use.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Tooltip, CircularProgress, useTheme, useMediaQuery, alpha, Snackbar, Alert,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import LocalCafeRoundedIcon from '@mui/icons-material/LocalCafeRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import { useTranslation } from 'react-i18next';

import presenceService from '../../services/presenceService';
import { authFetch } from '../../services/apiServices';

// Net worked milliseconds: (checkout|now − check-in) minus every break span.
function netWorkedMs(session, now) {
  if (!session?.check_in) return 0;
  const start = new Date(session.check_in).getTime();
  const end = session.check_out ? new Date(session.check_out).getTime() : now;
  let gross = end - start;
  let brk = 0;
  (session.breaks || []).forEach((b) => {
    const s = b.start ? new Date(b.start).getTime() : NaN;
    const e = b.end ? new Date(b.end).getTime() : (session.check_out ? new Date(session.check_out).getTime() : now);
    if (!Number.isNaN(s) && e > s) brk += e - s;
  });
  return Math.max(0, gross - brk);
}

function fmtDuration(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  // Always show seconds; add the hour segment only once there is one.
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// Open break = last entry with no `end`. Returns its type or null.
function openBreakType(session) {
  const breaks = session?.breaks || [];
  const last = breaks[breaks.length - 1];
  if (last && !last.end) return last.type || 'break';
  return null;
}

// Elapsed milliseconds of the currently open break (counts up live).
function openBreakElapsedMs(session, now) {
  const breaks = session?.breaks || [];
  const last = breaks[breaks.length - 1];
  if (last && last.start && !last.end) {
    const s = new Date(last.start).getTime();
    if (!Number.isNaN(s)) return Math.max(0, now - s);
  }
  return 0;
}

export default function WorkClock({ workerId }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [pauseAnchor, setPauseAnchor] = useState(null);
  const [startAnchor, setStartAnchor] = useState(null);
  const [endAnchor, setEndAnchor] = useState(null);
  const [error, setError] = useState('');
  // Break allowances from the worker's active schedule (minutes). null = no limit.
  const [limits, setLimits] = useState({ shortMin: null, longMin: null });
  // Per-worker toggle — some roles (e.g. Project Lead) don't use the time clock.
  const [hasTracker, setHasTracker] = useState(true);
  // Kanali WEB. E njëjta përgjigje e serverit që jep `has_time_tracker`; ora
  // fshihet kur web-i s'është kanal i lejuar, ose kur prezenca është e fikur
  // fare. `/api/attendanceEvents` e refuzon veprimin gjithsesi — kjo është
  // vetëm që butoni të mos premtojë diçka që do të refuzohet.
  const [webAllowed, setWebAllowed] = useState(true);
  const overSignaledRef = useRef(false);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!workerId) { setLoading(false); return; }
    try {
      const res = await authFetch(`/api/attendance/active-session?worker_id=${workerId}`);
      const json = await res.json().catch(() => ({}));
      if (mounted.current && json?.success) {
        setSession(json.data?.session || null);
        if (json.data?.has_time_tracker !== undefined) setHasTracker(json.data.has_time_tracker !== false);
        const allowed = json.data?.attendance_allowed;
        if (allowed && typeof allowed === 'object') setWebAllowed(allowed.WEB !== false);
      }
    } catch (_) { /* keep last known state */ }
    finally { if (mounted.current) setLoading(false); }
  }, [workerId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  // Re-sync when the tab regains focus (clock may have changed elsewhere).
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  // Tick the live timer every second while a session is open, so the header
  // clock advances in real time (seconds → minutes → hours).
  useEffect(() => {
    if (!session?.check_in || session?.check_out) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [session]);

  // Load the worker's scheduled break allowances so a running break can warn
  // (colour change) once it exceeds the planned duration.
  useEffect(() => {
    if (!workerId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`/api/workers/${workerId}/schedule`);
        const json = await res.json().catch(() => ({}));
        if (!alive || !json?.success || !json.data) return;
        const sb = Array.isArray(json.data.short_breaks) ? json.data.short_breaks : [];
        // Use the most generous configured short break as the per-break allowance.
        const shortMin = sb.reduce((mx, b) => Math.max(mx, Number(b?.min) || 0), 0) || null;
        const longMin = json.data.long_break_minutes != null ? Number(json.data.long_break_minutes) : null;
        setLimits({ shortMin, longMin });
      } catch (_) { /* no schedule → no limit */ }
    })();
    return () => { alive = false; };
  }, [workerId]);

  // Accepts one or more event types; they are sent in order so a combined
  // action like "check in AND start a break" works from a single click.
  const sendEvent = useCallback(async (...types) => {
    if (!workerId || busy || !types.length) return;
    setBusy(true);
    try {
      for (const type of types) {
        await presenceService().addEvent({ type, workerId });
      }
      await refresh();
    } catch (e) {
      if (mounted.current) setError(e?.message || 'Veprimi dështoi. Provo përsëri.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [workerId, busy, refresh]);

  // A short two-tone alert beep (Web Audio — no asset needed).
  const playBeep = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
      const beep = (freq, at, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain); gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
        osc.start(ctx.currentTime + at);
        osc.stop(ctx.currentTime + at + dur);
      };
      beep(880, 0, 0.18);
      beep(660, 0.22, 0.24);
      setTimeout(() => { ctx.close().catch(() => {}); }, 900);
    } catch (_) { /* audio unavailable */ }
  }, []);

  // Signal (beep + alert + browser notification) once the moment a break runs
  // over its planned limit. Guarded by a ref so it fires on the rising edge only.
  useEffect(() => {
    const bt = (session?.check_in && !session?.check_out) ? openBreakType(session) : null;
    const limitMin = bt === 'lunch' ? limits.longMin : bt === 'short' ? limits.shortMin : null;
    if (!bt || limitMin == null) { overSignaledRef.current = false; return; }
    const over = openBreakElapsedMs(session, now) > limitMin * 60000;
    if (over && !overSignaledRef.current) {
      overSignaledRef.current = true;
      playBeep();
      if ('Notification' in window && Notification.permission === 'granted') {
        try { new Notification('Pauza kaloi limitin', { body: 'Koha e pauzës ka tejkaluar planin e caktuar.' }); } catch (_) { /* noop */ }
      }
    } else if (!over) {
      overSignaledRef.current = false;
    }
  }, [session, now, limits, playBeep]);

  if (!workerId) return null;
  // Hidden entirely for workers whose time tracker is turned off.
  if (!loading && (!hasTracker || !webAllowed)) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={16} thickness={5} />
      </Box>
    );
  }

  const isOpen = !!session?.check_in && !session?.check_out;
  const breakType = isOpen ? openBreakType(session) : null;
  const onBreak = !!breakType;
  const worked = fmtDuration(netWorkedMs(session, now));
  // Live break counter — starts the moment a break begins.
  const breakElapsedMs = onBreak ? openBreakElapsedMs(session, now) : 0;
  const breakElapsed = onBreak ? fmtDuration(breakElapsedMs) : '';
  const breakLabel = breakType === 'lunch'
    ? t('workClock.onLongBreak', 'Në pauzë të gjatë')
    : breakType === 'short'
      ? t('workClock.onShortBreak', 'Në pauzë të shkurtër')
      : t('workClock.onBreak', 'Në pauzë');
  // Planned allowance for the current break (minutes) and whether it's exceeded.
  const breakLimitMin = breakType === 'lunch' ? limits.longMin
    : breakType === 'short' ? limits.shortMin
    : null;
  const breakLimitStr = breakLimitMin != null ? fmtDuration(breakLimitMin * 60000) : '';
  const overLimit = onBreak && breakLimitMin != null && breakElapsedMs > breakLimitMin * 60000;
  // How far past the allowance the break has run.
  const overByStr = overLimit ? fmtDuration(breakElapsedMs - breakLimitMin * 60000) : '';

  const errorSnack = (
    <Snackbar
      open={!!error}
      autoHideDuration={6000}
      onClose={() => setError('')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" variant="filled" onClose={() => setError('')} sx={{ width: '100%' }}>
        {error}
      </Alert>
    </Snackbar>
  );

  // ── Idle: not clocked in (or already finished today) ────────────────────────
  // Two direct actions — one click starts immediately, no menu, no second press:
  //   • Check-in  → clock in
  //   • Pauzë     → clock in AND start a short break (arrive already on break)
  // The caret next to Pauzë only offers the less-common "long break" variant.
  if (!isOpen) {
    const startMenu = (
      <Menu
        anchorEl={startAnchor}
        open={Boolean(startAnchor)}
        onClose={() => setStartAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.75, minWidth: 200, boxShadow: '0 10px 30px rgba(0,0,0,0.14)', '& .MuiMenuItem-root': { borderRadius: 1.5, mx: 0.5, my: 0.25 } } }}}
      >
        <MenuItem onClick={() => { setStartAnchor(null); sendEvent('checkin', 'short_break_start'); }}>
          <ListItemIcon><LocalCafeRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('workClock.shortBreak', 'Pauzë e shkurtër')} />
        </MenuItem>
        <MenuItem onClick={() => { setStartAnchor(null); sendEvent('checkin', 'lunch_start'); }}>
          <ListItemIcon><RestaurantRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('workClock.longBreak', 'Pauzë e gjatë')} />
        </MenuItem>
      </Menu>
    );

    const startBtn = compact ? (
      <Box sx={{ display: 'inline-flex', alignItems: 'stretch' }}>
        <Tooltip title={t('workClock.start', 'Fillo punën')} arrow>
          <IconButton color="success" onClick={() => sendEvent('checkin')} disabled={busy} aria-label={t('workClock.start', 'Fillo punën')} sx={{ pr: 0.25 }}>
            <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('workClock.startOptions', 'Opsione (Pauzë)')} arrow>
          <IconButton size="small" color="success" onClick={(e) => setStartAnchor(e.currentTarget)} disabled={busy} aria-label={t('workClock.startOptions', 'Opsione')} sx={{ ml: -1 }}>
            <ArrowDropDownRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      // One joined split pill: main = Check-in (1 click), ▾ = Pauzë options.
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'stretch',
          borderRadius: 999,
          boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.35)}`,
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="success"
          disableElevation
          startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrowRoundedIcon sx={{ color: '#fff' }} />}
          onClick={() => sendEvent('checkin')}
          disabled={busy}
          sx={{
            borderRadius: 999, borderTopRightRadius: 0, borderBottomRightRadius: 0,
            textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', pl: 1.75, pr: 1.25,
            color: '#fff', bgcolor: theme.palette.success.main,
            '&:hover': { bgcolor: theme.palette.success.dark },
            '& .MuiButton-startIcon': { color: '#fff' },
          }}
        >
          {t('workClock.start', 'Fillo punën')}
        </Button>
        <Tooltip title={t('workClock.startOptions', 'Opsione (Pauzë)')} arrow>
          <Button
            size="small"
            variant="contained"
            color="success"
            disableElevation
            onClick={(e) => setStartAnchor(e.currentTarget)}
            disabled={busy}
            aria-label={t('workClock.startOptions', 'Opsione (Pauzë)')}
            sx={{
              borderRadius: 999, borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
              px: 0.5, minWidth: 0, color: '#fff',
              bgcolor: theme.palette.success.main,
              '&:hover': { bgcolor: theme.palette.success.dark },
              borderLeft: `1px solid ${alpha('#fff', 0.35)}`,
            }}
          >
            <ArrowDropDownRoundedIcon sx={{ color: '#fff' }} />
          </Button>
        </Tooltip>
      </Box>
    );
    return <Box sx={{ display: 'flex', alignItems: 'center' }}>{startBtn}{startMenu}{errorSnack}</Box>;
  }

  // ── Active session: working or on a break ──
  // Amber while within the planned break; red once the break runs over.
  const accent = overLimit
    ? theme.palette.error.main
    : onBreak ? theme.palette.warning.main : theme.palette.success.main;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.375,
        pl: compact ? 0.75 : 1.25,
        pr: 0.5,
        py: 0.375,
        borderRadius: 999,
        bgcolor: alpha(accent, 0.1),
        border: `1px solid ${alpha(accent, 0.3)}`,
        boxShadow: `0 1px 3px ${alpha(accent, 0.18)}`,
        transition: 'background-color .25s ease, border-color .25s ease',
      }}
    >
      {/* Live status: compact single line — icon/dot + running timer.
          Full label lives in the tooltip to keep the pill narrow. */}
      <Tooltip
        arrow
        title={onBreak
          ? (overLimit ? `${breakLabel} · ${t('workClock.overLimit', 'mbi limit')} +${overByStr}` : breakLabel)
          : t('workClock.working', 'Duke punuar')}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, pr: 0.5, cursor: 'default' }}>
          {onBreak ? (
            breakType === 'lunch'
              ? <RestaurantRoundedIcon sx={{ fontSize: 16, color: accent, flexShrink: 0, animation: overLimit ? 'wc-blink .9s infinite' : 'none', '@keyframes wc-blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
              : <LocalCafeRoundedIcon sx={{ fontSize: 16, color: accent, flexShrink: 0, animation: overLimit ? 'wc-blink .9s infinite' : 'none', '@keyframes wc-blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
          ) : (
            <Box
              sx={{
                width: 9, height: 9, borderRadius: '50%', bgcolor: accent, flexShrink: 0,
                animation: 'wc-pulse 1.8s infinite',
                '@keyframes wc-pulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(accent, 0.5)}` },
                  '70%': { boxShadow: `0 0 0 6px ${alpha(accent, 0)}` },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(accent, 0)}` },
                },
              }}
            />
          )}
          <Box
            component="span"
            sx={{
              fontSize: '0.85rem', fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              letterSpacing: '0.01em', whiteSpace: 'nowrap',
              color: overLimit ? theme.palette.error.main : 'text.primary',
            }}
          >
            {!onBreak
              ? worked
              : overLimit
                ? `${breakElapsed} +${overByStr}`
                : (breakLimitStr ? `${breakElapsed} / ${breakLimitStr}` : breakElapsed)}
          </Box>
        </Box>
      </Tooltip>

      {/* Divider between status and actions */}
      <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.5, bgcolor: alpha(accent, 0.28) }} />

      {onBreak ? (
        // Resume — go straight back to work (clear labelled pill button)
        <Button
          size="small"
          variant="contained"
          color="success"
          disableElevation
          startIcon={busy ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <WorkRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />}
          onClick={() => sendEvent('break_end')}
          disabled={busy}
          sx={{
            borderRadius: 999, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap',
            py: 0.3, px: 1.25, minHeight: 0, fontSize: '0.75rem', ml: 0.25,
            color: '#fff',
            bgcolor: theme.palette.success.main,
            '&:hover': { bgcolor: theme.palette.success.dark },
            '& .MuiButton-startIcon': { color: '#fff' },
            boxShadow: `0 1px 4px ${alpha(theme.palette.success.main, 0.4)}`,
          }}
        >
          {t('workClock.resume', 'Vazhdo punën')}
        </Button>
      ) : (
        // Pause (opens short/long menu)
        <Tooltip title={t('workClock.pause', 'Pauzo')} arrow>
          <span>
            <IconButton
              size="small"
              onClick={(e) => setPauseAnchor(e.currentTarget)}
              disabled={busy}
              aria-label={t('workClock.pause', 'Pauzo')}
              sx={{
                color: theme.palette.warning.dark, width: 30, height: 30,
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.24) },
              }}
            >
              <PauseRoundedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* End work (opens a tiny confirm menu to avoid misclicks) */}
      <Tooltip title={t('workClock.end', 'Përfundo')} arrow>
        <span>
          <IconButton
            size="small"
            onClick={(e) => setEndAnchor(e.currentTarget)}
            disabled={busy}
            aria-label={t('workClock.end', 'Përfundo')}
            sx={{
              color: theme.palette.error.main, width: 30, height: 30, ml: 0.25,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) },
            }}
          >
            <StopRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Pause type menu */}
      <Menu
        anchorEl={pauseAnchor}
        open={Boolean(pauseAnchor)}
        onClose={() => setPauseAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.75, minWidth: 200, boxShadow: '0 10px 30px rgba(0,0,0,0.14)', '& .MuiMenuItem-root': { borderRadius: 1.5, mx: 0.5, my: 0.25 } } }}}
      >
        <MenuItem
          onClick={() => { setPauseAnchor(null); sendEvent('short_break_start'); }}
        >
          <ListItemIcon><LocalCafeRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('workClock.shortBreak', 'Pauzë e shkurtër')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setPauseAnchor(null); sendEvent('lunch_start'); }}
        >
          <ListItemIcon><RestaurantRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('workClock.longBreak', 'Pauzë e gjatë')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* End-work confirm menu */}
      <Menu
        anchorEl={endAnchor}
        open={Boolean(endAnchor)}
        onClose={() => setEndAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.75, minWidth: 190, boxShadow: '0 10px 30px rgba(0,0,0,0.14)', '& .MuiMenuItem-root': { borderRadius: 1.5, mx: 0.5, my: 0.25 } } }}}
      >
        <MenuItem
          onClick={() => { setEndAnchor(null); sendEvent('checkout'); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><StopRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('workClock.endConfirm', 'Përfundo punën')}</ListItemText>
        </MenuItem>
      </Menu>
      {errorSnack}
    </Box>
  );
}
